import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  Ticket,
  Plus,
  Loader2,
  Archive,
  Pencil,
  HelpCircle,
  Sparkles,
  Wand2,
  TicketPercent,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import { Progress } from '@/components/ui/progress'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import apiClient from '@/api/client'
import { formatCurrency, formatDateHuman } from '@/lib/utils'
import type { APIResponse, PromoCode } from '@/types'
import { MarketingShell } from './MarketingShell'
import { PromoCodeDrawer } from './PromoCodeDrawer'
import { AdminComebackCouponSettings } from './AdminComebackCouponSettings'
import { AdminRecoveryCouponSettings } from './AdminRecoveryCouponSettings'
import { RecoveryCouponDetailsDialog } from './RecoveryCouponDetailsDialog'

// Prefix the backend uses to flag auto-minted receipt thank-you codes —
// see backend/internal/handlers/marketing_promo.go:66 and the mint
// helper in marketing_promo_thank_you.go. Frontend mirrors the string
// so the admin UI can branch on it (Auto badge, drawer guard rails).
const THANK_YOU_DESCRIPTION_PREFIX = 'Receipt thank-you·'

function isAutoThankYouCode(p: PromoCode): boolean {
  return typeof p.description === 'string' && p.description.startsWith(THANK_YOU_DESCRIPTION_PREFIX)
}

function autoCodeOrderId(p: PromoCode): string | null {
  if (!isAutoThankYouCode(p)) return null
  const tail = p.description.slice(THANK_YOU_DESCRIPTION_PREFIX.length).trim()
  return tail || null
}

// Recovery coupons are manager-issued at the counter; the backend enriches
// their list row with a `recovery` block (customer/tier/reason/etc.) so we can
// show a legible label instead of the raw "Recovery·<orderUUID>" description.
const RECOVERY_DESCRIPTION_PREFIX = 'Recovery·'

function isRecoveryCode(p: PromoCode): boolean {
  return (
    p.recovery != null ||
    (typeof p.description === 'string' && p.description.startsWith(RECOVERY_DESCRIPTION_PREFIX))
  )
}

// Legible label for a recovery row: customer name → phone → short order ref.
function recoveryLabel(p: PromoCode): string {
  const r = p.recovery
  if (r?.customer_name) return r.customer_name
  if (r?.customer_phone) return r.customer_phone
  const orderId =
    r?.order_id ||
    (p.description.startsWith(RECOVERY_DESCRIPTION_PREFIX)
      ? p.description.slice(RECOVERY_DESCRIPTION_PREFIX.length).trim()
      : '')
  return orderId ? `Order ${orderId.slice(0, 8)}` : 'Recovery coupon'
}

function tierLabel(tier?: string): string {
  if (!tier) return ''
  return tier.charAt(0).toUpperCase() + tier.slice(1)
}

/** F-MKT-N4: a % code with more than one rate. */
function hasManyRates(p: PromoCode): boolean {
  return p.discount_type === 'percent' && p.scope === 'items' && ((p.rate_groups?.length ?? 0) > 0 || p.other_items_percent != null)
}

function formatDiscount(p: PromoCode): string {
  if (hasManyRates(p)) return 'Different rates'
  if (p.discount_type === 'percent') {
    const trimmed = String(Number(p.discount_value.toFixed(2)))
    return `${trimmed}% off`
  }
  return `${formatCurrency(p.discount_value)} off`
}

function formatValidity(p: PromoCode): string {
  if (!p.valid_from && !p.valid_to) return 'Always active'
  // Human, scannable dates ("Thu 11 Jun → Fri 11 Jul") instead of raw
  // M/D/YYYY. formatDateHuman returns "—" for a missing/blank side and adds
  // a "'yy" suffix only when the date falls outside the current year.
  return `${formatDateHuman(p.valid_from)} → ${formatDateHuman(p.valid_to)}`
}

function formatExpiryOnly(p: PromoCode): string {
  if (!p.valid_to) return '—'
  return formatDateHuman(p.valid_to)
}

/**
 * Admin catalog for promo codes — the cashier-typed discounts redeemable
 * at checkout. Two kinds of codes live here:
 *
 *   1. Manual marketing codes — you create them by hand for campaigns
 *      (Birthday discounts, Ramadan offers, influencer codes). The
 *      bulk of this page is built for these.
 *
 *   2. Auto-generated receipt thank-you codes — one per paid receipt,
 *      minted automatically when the feature is enabled in
 *      Settings → Receipt. Hidden by default to keep the catalog
 *      signal-heavy; toggle "Show auto-generated receipt codes" to
 *      audit them.
 */
export function AdminPromoCodes() {
  const [includeArchived, setIncludeArchived] = useState(false)
  const [includeReceiptCodes, setIncludeReceiptCodes] = useState(false)
  const [drawerMode, setDrawerMode] = useState<'create' | 'edit' | null>(null)
  const [editing, setEditing] = useState<PromoCode | null>(null)
  const [recoveryDetail, setRecoveryDetail] = useState<PromoCode | null>(null)

  const listQuery = useQuery<APIResponse<PromoCode[]>>({
    queryKey: ['promo-codes', { includeArchived, includeReceiptCodes }],
    queryFn: () =>
      apiClient.listPromoCodes({
        include_archived: includeArchived,
        include_receipt_codes: includeReceiptCodes,
      }),
    staleTime: 30_000,
  })

  const promos = listQuery.data?.data ?? []
  const { activeManual, activeAuto, archived } = useMemo(() => {
    const am: PromoCode[] = []
    const aa: PromoCode[] = []
    const r: PromoCode[] = []
    for (const p of promos) {
      if (!p.is_active) {
        r.push(p)
      } else if (isAutoThankYouCode(p)) {
        aa.push(p)
      } else {
        am.push(p)
      }
    }
    return { activeManual: am, activeAuto: aa, archived: r }
  }, [promos])

  function openEdit(p: PromoCode) {
    setEditing(p)
    setDrawerMode('edit')
  }

  const totallyEmpty =
    !listQuery.isLoading && activeManual.length === 0 && archived.length === 0 && activeAuto.length === 0

  return (
    <MarketingShell
      title="Promo codes"
      description="Discount codes your cashier can type at checkout. Create campaign codes by hand, or mint a comeback coupon automatically on every receipt."
    >
      <TooltipProvider delayDuration={150}>
        <div className="space-y-6">
          {/* Zone 1 — Campaign codes. The page's primary job: codes your
              cashier types at checkout. Leads the page so an owner who just
              wants to make "RAMADAN200" isn't scrolling past automation
              panels first. */}

          {/* Header row — primary action on the left, view options on the right.
              Keeps the page's main verb ("New code") visually dominant and
              tucks the noise filter into a small cluster. */}
          <div className="flex flex-wrap items-center justify-between gap-3">
            <Button
              type="button"
              size="sm"
              onClick={() => {
                setEditing(null)
                setDrawerMode('create')
              }}
              className="gap-1.5"
            >
              <Plus className="h-4 w-4" />
              New code
            </Button>
            <div className="flex items-center gap-1 rounded-md border border-border/60 bg-muted/30 px-2 py-1.5">
              <span className="px-1 text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                View
              </span>
              <ViewToggle
                id="show-archived"
                label="Show archived"
                checked={includeArchived}
                onCheckedChange={setIncludeArchived}
              />
            </div>
          </div>

          {/* Empty state — first run. Make the "what is this for?" obvious
              instead of dumping an empty table. */}
          {totallyEmpty && (
            <EmptyState
              onCreate={() => {
                setEditing(null)
                setDrawerMode('create')
              }}
            />
          )}

          {!totallyEmpty && (
            <PromoSection
              title="Active codes"
              icon={Ticket}
              codes={activeManual}
              loading={listQuery.isLoading}
              onEdit={openEdit}
              onOpenRecovery={setRecoveryDetail}
              emptyState="No active marketing codes. Create one to start a campaign."
            />
          )}

          {includeArchived && archived.length > 0 && (
            <PromoSection
              title="Archived"
              icon={Archive}
              codes={archived}
              loading={false}
              onEdit={openEdit}
              onOpenRecovery={setRecoveryDetail}
              emptyState=""
              archivedTone
            />
          )}

          {/* Zone 2 — Automatic coupons. Codes the system hands out for you,
              so they live below the codes you create by hand. Both cards are
              collapsed by default and show a one-line summary. */}
          <div className="space-y-4 border-t border-border/60 pt-6">
            <div>
              <h2 className="text-base font-semibold text-foreground">Automatic coupons</h2>
              <p className="text-sm text-muted-foreground">
                Coupons the system hands out for you — no codes to create. Turn on what you need.
              </p>
            </div>

            {/* Comeback coupon — auto-minted single-use code on every paid
                receipt. The master toggle is one click away and the live
                preview makes the result tangible. */}
            <AdminComebackCouponSettings
              onViewPastCodes={() => setIncludeReceiptCodes((v) => !v)}
              pastCodesVisible={includeReceiptCodes}
            />

            {/* Recovery coupon — manager-issued comeback code for service
                recovery. Configured here; issued from the counter rail behind
                a manager PIN. */}
            <AdminRecoveryCouponSettings />

            {includeReceiptCodes && (
              <PromoSection
                title="Past comeback codes"
                icon={Sparkles}
                codes={activeAuto}
                loading={listQuery.isLoading}
                onEdit={openEdit}
                emptyState="No comeback codes yet — they appear here as each paid receipt prints (turn the feature on above)."
                autoSection
                banner="These codes are minted automatically — one per receipt. To change the discount or validity for future codes, use the Comeback coupon settings above."
              />
            )}
          </div>
        </div>
      </TooltipProvider>

      <PromoCodeDrawer
        mode={drawerMode ?? 'create'}
        initial={editing}
        open={drawerMode !== null}
        onOpenChange={(open) => {
          if (!open) {
            setDrawerMode(null)
            setEditing(null)
          }
        }}
      />

      <RecoveryCouponDetailsDialog
        promo={recoveryDetail}
        open={recoveryDetail !== null}
        onOpenChange={(open) => {
          if (!open) setRecoveryDetail(null)
        }}
      />
    </MarketingShell>
  )
}

function ViewToggle({
  id,
  label,
  checked,
  onCheckedChange,
  tip,
}: {
  id: string
  label: string
  checked: boolean
  onCheckedChange: (v: boolean) => void
  tip?: string
}) {
  return (
    <label
      htmlFor={id}
      className="flex cursor-pointer items-center gap-1.5 rounded px-1.5 py-0.5 text-sm text-muted-foreground hover:text-foreground"
    >
      <Switch
        id={id}
        checked={checked}
        onCheckedChange={onCheckedChange}
        className="h-4 w-7 [&>span]:h-3 [&>span]:w-3 data-[state=checked]:[&>span]:translate-x-3"
      />
      <span>{label}</span>
      {tip && (
        <Tooltip>
          <TooltipTrigger asChild>
            <HelpCircle className="h-3 w-3 text-muted-foreground/70 hover:text-foreground" />
          </TooltipTrigger>
          <TooltipContent side="bottom" className="max-w-xs text-sm">
            {tip}
          </TooltipContent>
        </Tooltip>
      )}
    </label>
  )
}

function EmptyState({ onCreate }: { onCreate: () => void }) {
  return (
    <div className="rounded-xl border border-dashed border-border/80 bg-muted/20 p-8 text-center">
      <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
        <TicketPercent className="h-6 w-6" aria-hidden />
      </div>
      <h3 className="text-base font-semibold text-foreground">No promo codes yet</h3>
      <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">
        Promo codes are discount shortcuts your cashier types at checkout. Use them for
        seasonal offers, birthday rewards, or influencer campaigns.
      </p>
      <div className="mx-auto mt-5 flex max-w-xl flex-wrap items-center justify-center gap-2">
        <ExampleChip code="BIRTHDAY10" discount="10% off" />
        <ExampleChip code="RAMADAN200" discount="Rs. 200 off" />
        <ExampleChip code="VIP25" discount="25% off, single use" />
      </div>
      <div className="mt-6">
        <Button onClick={onCreate} className="gap-1.5">
          <Plus className="h-4 w-4" />
          Create your first code
        </Button>
      </div>
      <p className="mt-5 text-sm text-muted-foreground">
        Tip: you can also auto-print a unique coupon on every receipt —{' '}
        <a href="#comeback-coupon" className="underline">
          set up your Comeback coupon below
        </a>
        .
      </p>
    </div>
  )
}

function ExampleChip({ code, discount }: { code: string; discount: string }) {
  return (
    <div className="inline-flex items-center gap-2 rounded-full border border-border/70 bg-background px-3 py-1 text-sm">
      <span className="font-mono font-semibold text-foreground">{code}</span>
      <span className="text-muted-foreground">{discount}</span>
    </div>
  )
}

function PromoSection({
  title,
  icon: Icon,
  codes,
  loading,
  onEdit,
  onOpenRecovery,
  emptyState,
  archivedTone,
  autoSection,
  banner,
}: {
  title: string
  icon: typeof Ticket
  codes: PromoCode[]
  loading: boolean
  onEdit: (p: PromoCode) => void
  onOpenRecovery?: (p: PromoCode) => void
  emptyState: string
  archivedTone?: boolean
  autoSection?: boolean
  banner?: string
}) {
  return (
    <div className={`rounded-lg border border-border/80 ${archivedTone ? 'bg-muted/30' : ''}`}>
      <div className="flex items-center gap-2 border-b border-border/60 bg-muted/40 px-4 py-2.5">
        <Icon className="h-4 w-4 text-muted-foreground" aria-hidden />
        <h3 className="text-base font-semibold text-foreground">{title}</h3>
        <Badge variant="outline" className="ml-1 text-sm tabular-nums">
          {codes.length}
        </Badge>
      </div>
      {banner && (
        <div className="border-b border-border/60 bg-amber-50/60 px-4 py-2 text-sm leading-snug text-amber-900 dark:bg-amber-950/40 dark:text-amber-100">
          <Wand2 className="-mt-0.5 mr-1 inline-block h-3 w-3 align-middle" />
          {banner}
        </div>
      )}
      {loading ? (
        <div className="flex h-24 items-center justify-center">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      ) : codes.length === 0 ? (
        <div className="px-4 py-8 text-center text-sm text-muted-foreground">
          {emptyState}
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow className="bg-transparent hover:bg-transparent">
              <TableHead className="w-[22%]">Code</TableHead>
              <TableHead>Description</TableHead>
              <TableHead className="w-[12%]">Discount</TableHead>
              <TableHead className="w-[15%] hidden md:table-cell">
                {autoSection ? 'Expires' : 'Validity'}
              </TableHead>
              <TableHead className="w-[14%] text-right">Redemptions</TableHead>
              <TableHead className="w-[12%] text-right hidden sm:table-cell">Revenue</TableHead>
              <TableHead className="w-[6%]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {codes.map((p) => (
              <PromoRow
                key={p.id}
                promo={p}
                onEdit={onEdit}
                onOpenRecovery={onOpenRecovery}
                archivedTone={archivedTone}
                autoSection={autoSection}
              />
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  )
}

function PromoRow({
  promo,
  onEdit,
  onOpenRecovery,
  archivedTone,
  autoSection,
}: {
  promo: PromoCode
  onEdit: (p: PromoCode) => void
  onOpenRecovery?: (p: PromoCode) => void
  archivedTone?: boolean
  autoSection?: boolean
}) {
  const isAuto = isAutoThankYouCode(promo)
  const isRecovery = isRecoveryCode(promo)
  const orderId = autoCodeOrderId(promo)
  const showProgress = !isAuto && promo.max_redemptions != null
  const pct =
    showProgress && promo.max_redemptions
      ? Math.min(100, Math.round((promo.redemption_count / promo.max_redemptions) * 100))
      : 0
  return (
    <TableRow className={archivedTone ? 'opacity-70' : ''}>
      <TableCell
        className={`font-mono text-sm ${
          isAuto ? 'font-medium text-muted-foreground' : 'font-semibold'
        }`}
      >
        {promo.code}
      </TableCell>
      <TableCell className="text-sm">
        {isRecovery ? (
          <button
            type="button"
            onClick={() => onOpenRecovery?.(promo)}
            className="group flex min-w-0 max-w-xs items-center gap-2 text-left"
            title="View recovery coupon details"
          >
            <Badge
              variant="secondary"
              className="shrink-0 border border-amber-400/50 bg-amber-50 text-sm text-amber-700 dark:bg-amber-950/40 dark:text-amber-300"
            >
              Recovery
            </Badge>
            <span className="truncate font-medium text-foreground underline-offset-2 group-hover:underline">
              {recoveryLabel(promo)}
            </span>
            {promo.recovery?.tier && (
              <span className="shrink-0 text-sm text-muted-foreground">
                · {tierLabel(promo.recovery.tier)}
              </span>
            )}
          </button>
        ) : isAuto ? (
          <div className="flex items-center gap-2">
            <Badge variant="secondary" className="text-sm">
              Auto
            </Badge>
            {orderId && (
              <span className="truncate text-sm text-muted-foreground">
                Order {orderId.slice(0, 8)}
              </span>
            )}
          </div>
        ) : promo.description ? (
          <span className="inline-block max-w-xs truncate align-bottom">{promo.description}</span>
        ) : (
          <span className="italic text-muted-foreground">No description</span>
        )}
      </TableCell>
      <TableCell className="text-sm">
        <div>{formatDiscount(promo)}</div>
        {promo.scope === 'items' &&
          (promo.category_ids.length + promo.product_ids.length === 0 && !hasManyRates(promo) ? (
            <div className="text-sm text-amber-700 dark:text-amber-400">
              Its items were removed from the menu — edit or archive
            </div>
          ) : (
            <div className="max-w-[14rem] truncate text-sm text-muted-foreground" title={promo.applies_to}>
              {hasManyRates(promo) ? promo.applies_to : `on ${promo.applies_to || 'selected items'}`}
            </div>
          ))}
      </TableCell>
      <TableCell className="text-sm hidden md:table-cell">
        {autoSection ? formatExpiryOnly(promo) : formatValidity(promo)}
      </TableCell>
      <TableCell className="text-right text-sm tabular-nums">
        {promo.max_redemptions != null
          ? `${promo.redemption_count.toLocaleString()} / ${promo.max_redemptions.toLocaleString()}`
          : `${promo.redemption_count.toLocaleString()} / ∞`}
        {showProgress && (
          <Progress
            value={pct}
            className="ml-auto mt-1 h-1 w-16 sm:w-20"
            aria-label="Redemption progress"
          />
        )}
      </TableCell>
      <TableCell className="text-right text-sm tabular-nums hidden sm:table-cell">
        {formatCurrency(promo.revenue_attributed_pkr)}
      </TableCell>
      <TableCell className="text-right">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => onEdit(promo)}
          className="h-7 gap-1.5 text-sm"
        >
          <Pencil className="h-3 w-3" />
          {isAuto ? 'View' : 'Edit'}
        </Button>
      </TableCell>
    </TableRow>
  )
}
